import React, { ReactElement, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { QueryClient, QueryClientProvider } from 'react-query';
import { css } from '@emotion/react';
import { Breadcrumbs, Grid, StructuredData } from '@gumtree/ui-library';
import PartnershipAdSlot from '@gumtree/third-party-ads/src/framework/partnership/partnership-ad-slot';
import AdSlot from '@gumtree/third-party-ads/display-ad-slot';
import {
    L3CategoryIds,
    LeafCategoryIdsWithPhones,
    L1CategoryIds,
    L2CategoryIds,
} from '@gumtree/shared/src/types/categories';
import { getL1CategoryIdBySeoName } from '@gumtree/shared/src/util/category-mapping';
import { DisplayAdSlotDivNames } from '@gumtree/third-party-ads/src/framework/dfp/ad-config/slot-configs';
import { useFeatureValue } from '@growthbook/growthbook-react';
import { GrowthBookFeature } from '@gumtree/shared/src/model/experiment';
import { validateOrigin } from '@gumtree/shared/src/util/message-helpers';
import { trackGA4Event } from '@gumtree/shared/src/util/ga4-shared';
import { FashionAttributeKeys } from '@gumtree/syi/src/components/panels/fashion';
import { SimilarAd } from '@gumtree/shared/src/model/similar-ad.model';
import { setCustomAttributes } from '@gumtree/shell/src/braze';
import BuyerProtectionDialog from '@gumtree/ui-library/src/buyer-protection-dialog/buyer-protection-dialog';

import ImagesMapTabs from './components/images-map-tabs';
import HeaderBlock from './components/header-block/index';
import StaySafe from './components/stay-safe';
import SimilarAdsCarousel from './components/similar-ads-carousel';
import SharePanel from './components/share-panel/container';
import Description from './components/description/index';
import Attributes from './components/attributes/index';
import { HiddenSeoTitle } from './components/attributes/index.style';
import PetsAdvicePanel from './components/pets/pets-advice-panel';
import SellerContactCard from './components/seller-contact-card';
import SellerContactMobileBlock from './components/seller-contact-block/mobile-block/mobile-block';
import MessageEncourage from './components/seller-contact-block/message-encourage/message-encourage';
import Disclaimer from './components/disclaimer';
import SeoL1Category from './components/seo-l1-category';
import StickyHeader from './components/sticky-header';
import BuyStickyHeader from './components/sticky-header/buy-sticky-header';
import VHCPlaceholder from './components/vhc/vhc-placeholder';
import VHCPanel from './components/vhc/panel';
import MobileGallery from './components/vip-gallery';
import MapTab from './components/images-map-tabs/map-tab';
import VipSkills from './components/vip-skills';

import './app.scss';
import {
    AdBreadcrumbSlot,
    CompactPartnershipAdsContainer,
    DefaultPartnershipAdsContainer,
    DesktopIframeTopBorder,
    RightPanel,
    SimilarAdsCarouselContainer,
    SmallDesktopVhc,
    SocialPanelLeft,
    SocialPanelRight,
    StaySafeContainer,
    StickyFooter,
    VhcPanelContainer,
    VipAdInfo,
    VipAdMap,
    VipAdSocial,
    VipAdTitle,
    VipCarouselContainer,
    VipContainer,
    VipContent,
    SlotsWrapper,
} from './app.style';
import MotorsSpecs from './components/motors-specs/motors-specs';
import SellOneLink from './components/sell-one-link';
import MadgexConfirm from './components/madgex/madgex-confirm';
import PetsAttributes from './components/pets-attributes';
import AttributesWithIcon from './components/phones-attributes';
import { getPopulatedSchema } from './seo-utils';
import { selectState, selectPartnershipParams } from './selectors';
import type { VipState } from './reducers';
import { MotorsCTAContext } from './vip-context';
import { renderAdSlot, usePartnershipAdFlags } from './utils/adUtils';

const queryClient = new QueryClient();

const COMMON_ATTRIBUTES = ['Posted', 'seller_type'];

const onlyHasCommonAttributes = (attributes: string[]) => {
    return attributes.every((attribute) => COMMON_ATTRIBUTES.includes(attribute));
};

export const renderVipAdInfo = ({
    isServices,
    isMobileCardsLayout,
    isMotors,
    isForSale,
    isJobs,
    isMobile,
    partnershipParams,
    adDetails,
    baseConfig,
    isPartnerAdDefault1Gam,
    isPets,
    isPhonesForSale,
    isUserLoggedIn,
    sellerSkills,
    isDiyTools,
    isHomeGarden,
}: {
    isServices: boolean;
    isMobileCardsLayout: boolean;
    isMotors: boolean;
    isForSale: boolean;
    isJobs: boolean;
    isMobile: boolean;
    partnershipParams: any;
    adDetails: VipState['adDetails'];
    baseConfig: VipState['baseConfig'];
    isPartnerAdDefault1Gam: boolean;
    isPets: boolean;
    isUserLoggedIn: boolean;
    isPhonesForSale: boolean;
    sellerSkills;
    isDiyTools: boolean;
    isHomeGarden: boolean;
}) => {
    const attributes = Array.isArray(adDetails.attributes)
        ? adDetails.attributes.filter(
              (attr) => attr.key !== 'support_shipping' && attr.key !== 'parcel_size'
          )
        : adDetails.attributes;

    const isFasion =
        Array.isArray(attributes) &&
        attributes.length > 0 &&
        !onlyHasCommonAttributes(attributes.map((attr) => attr.key)) &&
        attributes.every(
            (attr) =>
                COMMON_ATTRIBUTES.includes(attr.key) || FashionAttributeKeys.includes(attr.key)
        );
    const showIconAttrs = isPhonesForSale || isDiyTools || isHomeGarden || isFasion;
    const showSpecailAttrs = isPets || showIconAttrs;

    return (
        <VipAdInfo className="grid-info" isDesktop={!isMobile}>
            {isMotors && <HiddenSeoTitle>Vehicle information</HiddenSeoTitle>}

            {!isMobileCardsLayout && !showSpecailAttrs && (
                <Attributes
                    isMobileCardsLayout={isMobileCardsLayout}
                    l1CategorySeoName={adDetails.l1CategorySeoName}
                    attributes={attributes}
                >
                    {isForSale && (
                        <SellOneLink
                            isUserLoggedIn={isUserLoggedIn}
                            sellOneUrl={`${baseConfig.bffUrl}/postad/create?categoryId=${adDetails?.leafCategoryId}`}
                        />
                    )}
                </Attributes>
            )}

            {isJobs && isMobile && <SellerContactCard isMobileCardsLayout={isMobileCardsLayout} />}

            {isServices && sellerSkills.length > 0 && (
                <VipSkills title="Services" data={sellerSkills} />
            )}

            {!isMobileCardsLayout && (
                <>
                    <Description
                        isMotors={isMotors}
                        isPetsOrPhones={showSpecailAttrs}
                        linesToShow={8}
                        showIcon={false}
                    />
                    {isPets && <PetsAttributes />}
                    {showIconAttrs && (
                        <AttributesWithIcon>
                            {isForSale && (
                                <SellOneLink
                                    isUserLoggedIn={isUserLoggedIn}
                                    sellOneUrl={`${baseConfig.bffUrl}/postad/create?categoryId=${adDetails?.leafCategoryId}`}
                                />
                            )}
                        </AttributesWithIcon>
                    )}
                    {isMotors && <MotorsSpecs />}

                    {isPartnerAdDefault1Gam ? (
                        <div style={{ width: '100%' }}>
                            <AdSlot id={DisplayAdSlotDivNames.partnershipBottom} />
                        </div>
                    ) : (
                        <PartnershipAdSlot
                            slotId="partnerAdDefault1"
                            style={isMotors ? DesktopIframeTopBorder : undefined}
                            {...partnershipParams}
                        />
                    )}
                </>
            )}

            <PartnershipAdSlot slotId="partnerAdDefault2" {...partnershipParams} />
            {!isMobileCardsLayout && (
                <>
                    {renderAdSlot(
                        DisplayAdSlotDivNames.vipMiddleMobile,
                        adDetails.l1CategorySeoName,
                        baseConfig.device.type,
                        css`
                            margin: 18px 0;
                        `
                    )}

                    {adDetails.showLegalDisclaimer && <Disclaimer />}
                    <AdSlot id={DisplayAdSlotDivNames.textLink} />
                    <AdSlot id={DisplayAdSlotDivNames.textLinkBase} />
                </>
            )}
        </VipAdInfo>
    );
};

export const RightPanelContainer = ({
    isMobileCardsLayout,
    isMobile,
    isMotors,
    isPets,
    isPhonesForSale,
    partnershipParams,
    adDetails,
    vhcData,
    baseConfig,
    isPartnerAdDefault1Gam,
    mediaArray,
    showMapConditions,
    isDefaultPartnershipBelowMap,
    isJobs,
    isForSale,
    mapDetails,
    isPurchaseAvailable,
    isDiyTools,
    isHomeGarden,
    setIsBuyerProtectionOpen,
}: {
    isMobileCardsLayout: boolean;
    isMobile: boolean;
    isMotors: boolean;
    isPets: boolean;
    isPhonesForSale: boolean;
    partnershipParams: any;
    adDetails: VipState['adDetails'];
    vhcData: any;
    baseConfig: VipState['baseConfig'];
    isPartnerAdDefault1Gam: boolean;
    mediaArray: any[];
    showMapConditions: boolean;
    isDefaultPartnershipBelowMap: boolean;
    isJobs: boolean;
    isForSale: boolean;
    mapDetails: VipState['mapDetails'];
    isDiyTools: boolean;
    isHomeGarden: boolean;
    isPurchaseAvailable?: boolean;
    setIsBuyerProtectionOpen: (isOpen: boolean) => void;
}): ReactElement => {
    const partnershipDefaultRef = useRef<HTMLDivElement>(null);
    const attributes = adDetails.attributes;
    const isFasion =
        Array.isArray(attributes) &&
        attributes.length > 0 &&
        !onlyHasCommonAttributes(attributes.map((attr) => attr.key)) &&
        attributes.every(
            (attr) =>
                COMMON_ATTRIBUTES.includes(attr.key) || FashionAttributeKeys.includes(attr.key)
        );

    const showIconAttrs = isPhonesForSale || isDiyTools || isHomeGarden || isFasion;
    const showSpecailAttrs = isPets || showIconAttrs;

    useEffect(() => {
        if (!(isMotors && isMobileCardsLayout)) {
            return;
        } else {
            const observer = new IntersectionObserver(
                (entries, obs) => {
                    entries.forEach((entry) => {
                        if (entry.isIntersecting) {
                            trackGA4Event<GA4.FinanceCalculatorView>({
                                event: 'finance_calculator_view',
                            });
                            obs.unobserve(entry.target);
                        }
                    });
                },
                { threshold: 0.4 }
            );

            if (partnershipDefaultRef?.current) {
                observer.observe(partnershipDefaultRef.current);
            }

            return () => {
                observer.disconnect();
            };
        }
    }, []);

    return (
        <RightPanel
            item
            colL={isMobile ? undefined : 6}
            colXl={isMobile ? undefined : 4}
            isMobileCardsLayout={isMobileCardsLayout}
            isDesktop={!isMobile}
        >
            {isMobileCardsLayout ? (
                <>
                    {isMotors && (
                        <>
                            <Description
                                isMobileCardsLayout={isMobileCardsLayout}
                                useToggle
                                isMotors={isMotors}
                                isPetsOrPhones={showSpecailAttrs}
                            />
                            <MotorsSpecs />
                            <AdSlot
                                id={DisplayAdSlotDivNames.vipMiddleMobile}
                                css={css`
                                    margin: 0;
                                `}
                            />
                        </>
                    )}
                    {!isJobs && (
                        <SellerContactCard
                            isMobileCardsLayout={isMobileCardsLayout}
                            isPurchaseAvailable={isPurchaseAvailable}
                            setIsBuyerProtectionOpen={setIsBuyerProtectionOpen}
                        />
                    )}
                    <SharePanel isMobileCardsLayout={isMobileCardsLayout} />
                    {!isMotors && (
                        <Description
                            isMobileCardsLayout={isMobileCardsLayout}
                            useToggle
                            isMotors={isMotors}
                            isForSale={isForSale}
                            isPetsOrPhones={isPets || isPhonesForSale}
                        />
                    )}
                    {isPets && <PetsAttributes isMobileCardsLayout />}
                    {showIconAttrs && <AttributesWithIcon isMobileCardsLayout />}
                    {(function conditionallyReverseArray(array: any[], condition: boolean) {
                        return condition ? array.slice().reverse() : array;
                    })(
                        [
                            isPartnerAdDefault1Gam ? (
                                <div style={{ width: '100%' }}>
                                    <AdSlot id={DisplayAdSlotDivNames.partnershipBottom} />
                                </div>
                            ) : (
                                <DefaultPartnershipAdsContainer
                                    key={0}
                                    isMobileCardsLayout={isMobileCardsLayout}
                                    id="partnership-default-container"
                                    ref={partnershipDefaultRef}
                                >
                                    <PartnershipAdSlot
                                        slotId="partnerAdDefault1"
                                        {...partnershipParams}
                                    />
                                </DefaultPartnershipAdsContainer>
                            ),
                            showMapConditions && mediaArray?.length > 0 && (
                                <MapTab
                                    key={1}
                                    isMobileCardsLayout={isMobileCardsLayout}
                                    mapDetails={mapDetails}
                                    baseConfig={baseConfig}
                                />
                            ),
                        ],
                        isDefaultPartnershipBelowMap
                    )}
                    {isMotors && Object.keys(vhcData).length > 0 && (
                        <VhcPanelContainer isMobileCardsLayout={isMobileCardsLayout}>
                            {vhcData?.checked ? <VHCPanel /> : <VHCPlaceholder />}
                        </VhcPanelContainer>
                    )}
                </>
            ) : (
                <>
                    <SellerContactCard
                        isMobileCardsLayout={isMobileCardsLayout}
                        isPurchaseAvailable={isPurchaseAvailable}
                        setIsBuyerProtectionOpen={setIsBuyerProtectionOpen}
                    />
                    {renderAdSlot(
                        DisplayAdSlotDivNames.mpu,
                        adDetails.l1CategorySeoName,
                        baseConfig.device.type,
                        css`
                            margin: 18px 0;
                        `
                    )}
                    <SmallDesktopVhc /** Desktop less than medium */
                        isMotors={isMotors}
                        isDesktop={!isMobile}
                    >
                        {Object.keys(vhcData).length > 0 && (
                            <VhcPanelContainer>
                                {vhcData?.checked ? <VHCPanel /> : <VHCPlaceholder />}
                            </VhcPanelContainer>
                        )}
                    </SmallDesktopVhc>

                    <SocialPanelRight /** Only Desktop from medium */>
                        {isMotors && Object.keys(vhcData).length > 0 && (
                            <VhcPanelContainer>
                                {vhcData?.checked ? <VHCPanel /> : <VHCPlaceholder />}
                            </VhcPanelContainer>
                        )}
                        <VipAdSocial>
                            <SharePanel />
                        </VipAdSocial>
                        {adDetails?.l1Category === 2526 && <PetsAdvicePanel />}
                        <div
                            className="vip-ad-carousel"
                            {...(baseConfig?.device?.type === 'desktop' && {
                                'data-q': 'stay-safe-desktop',
                            })}
                        >
                            <StaySafe />
                        </div>
                        {renderAdSlot(
                            DisplayAdSlotDivNames.vipMiddleDesktop,
                            adDetails.l1CategorySeoName,
                            baseConfig.device.type,
                            css`
                                margin: 18px 0;
                                width: 100%;
                            `
                        )}
                    </SocialPanelRight>
                </>
            )}
        </RightPanel>
    );
};

export const renderVipCarouselContainer = ({
    isMobileCardsLayout,
    isMobile,
    adDetails,
    mapDetails,
    isPartnerAdCompact1Gam,
    isPartnerAdCompact2Gam,
    partnershipParams,
    hasImages,
    hasVideo,
    isJobs,
    replyEmail,
    isMotors,
    isSellerCinchCar,
    isPurchaseAvailable,
    monthlyPrice,
    compactUrl,
    compact2IframeRef,
    sellerType,
    setIsBuyerProtectionOpen,
}: {
    isMobileCardsLayout: boolean;
    isMobile: boolean;
    adDetails: any;
    mapDetails: any;
    isPartnerAdCompact1Gam: boolean;
    isPartnerAdCompact2Gam: boolean;
    partnershipParams: any;
    hasImages: boolean;
    hasVideo: boolean;
    isJobs: boolean;
    replyEmail: string | undefined;
    isMotors: boolean;
    isSellerCinchCar: boolean;
    isPurchaseAvailable?: boolean;
    monthlyPrice: number | null;
    compactUrl: string | null;
    compact2IframeRef: React.RefObject<HTMLIFrameElement>;
    sellerType: string | null;
    setIsBuyerProtectionOpen: (isOpen: boolean) => void;
}) => {
    const galleryAndMapsCombo = (
        <VipAdMap isMobileCardsLayout={isMobileCardsLayout}>
            <ImagesMapTabs />
        </VipAdMap>
    );

    const partnershipAdSlots =
        isPartnerAdCompact1Gam && isPartnerAdCompact2Gam ? (
            <SlotsWrapper>
                <AdSlot id={DisplayAdSlotDivNames.partnershipWidget} />
                <AdSlot id={DisplayAdSlotDivNames.partnershipWidget1} />
            </SlotsWrapper>
        ) : (
            <>
                {isMotors && isMobile ? (
                    <>
                        <CompactPartnershipAdsContainer
                            isMobileCardsLayout={isMobileCardsLayout}
                            id="partnership-compact-container"
                            data-testid="partnership-compact-container"
                        >
                            {isPartnerAdCompact2Gam ? (
                                <AdSlot id={DisplayAdSlotDivNames.partnershipWidget1} />
                            ) : (
                                <PartnershipAdSlot
                                    slotId="partnerAdCompact2"
                                    {...partnershipParams}
                                />
                            )}
                        </CompactPartnershipAdsContainer>
                        <CompactPartnershipAdsContainer
                            isMobileCardsLayout={isMobileCardsLayout}
                            id="partnership-compact-container-split"
                            data-testid="partnership-compact-container-split"
                        >
                            <PartnershipAdSlot slotId="partnerAdCompact1" {...partnershipParams} />
                        </CompactPartnershipAdsContainer>
                    </>
                ) : (
                    <CompactPartnershipAdsContainer
                        isMobileCardsLayout={isMobileCardsLayout}
                        id="partnership-compact-container"
                        data-testid="partnership-compact-container"
                    >
                        <PartnershipAdSlot slotId="partnerAdCompact1" {...partnershipParams} />
                        {isPartnerAdCompact2Gam ? (
                            <AdSlot id={DisplayAdSlotDivNames.partnershipWidget1} />
                        ) : (
                            <PartnershipAdSlot
                                slotId="partnerAdCompact2"
                                ref={compact2IframeRef}
                                {...partnershipParams}
                            />
                        )}
                    </CompactPartnershipAdsContainer>
                )}
            </>
        );

    const adTitleHeaderBlock = (
        <VipAdTitle isMobileCardsLayout={isMobileCardsLayout}>
            <HeaderBlock
                {...adDetails}
                isMobileCardsLayout={isMobileCardsLayout}
                visibleOnMap={mapDetails.visibleOnMap}
                isSellerCinchCar={isSellerCinchCar}
                isPurchaseAvailable={isPurchaseAvailable}
                monthlyPrice={monthlyPrice}
                compactUrl={compactUrl}
                sellerType={sellerType}
                setIsBuyerProtectionOpen={setIsBuyerProtectionOpen}
            />
        </VipAdTitle>
    );

    return (
        <VipCarouselContainer
            item
            colXl={isMobile ? undefined : 8}
            isMobileCardsLayout={isMobileCardsLayout}
        >
            {isMobileCardsLayout ? (
                <>
                    {!isJobs && !hasImages && !hasVideo && galleryAndMapsCombo}
                    {!isJobs && <MobileGallery />}
                    {adTitleHeaderBlock}
                    {partnershipAdSlots}
                    {!!replyEmail && <SellerContactMobileBlock />}
                    {!isMotors && (
                        <AdSlot
                            id={DisplayAdSlotDivNames.vipMiddleMobile}
                            css={css`
                                margin: 0;
                            `}
                        />
                    )}
                </>
            ) : (
                <>
                    {adTitleHeaderBlock}
                    {partnershipAdSlots}
                    {!isJobs && galleryAndMapsCombo}
                </>
            )}
        </VipCarouselContainer>
    );
};

const setCustomAttributeOnBraze = (similarAds: SimilarAd[], adId: number) => {
    adId && setCustomAttributes('last_viewed_advert_id', adId);

    const value = similarAds?.map((adItem: SimilarAd) => {
        const target = {
            adId: adItem.id,
            adUrl: `${window.location.origin}${adItem.path}`,
            imageUrl: adItem.imageUrl ?? '',
            price: adItem.price,
            title: adItem.title,
        };
        return target;
    });
    value?.length > 0 && setCustomAttributes('similar_items', value.slice(0, 4));
};

const App: React.FC = () => {
    const {
        adDetails,
        baseConfig,
        breadcrumbs,
        sellerSkills,
        mapDetails,
        vhcData,
        replyEmail,
        images,
        video,
        userData,
        isSellerCinchCar,
        similarAds,
        postCall,
    } = useSelector(selectState);
    const [monthlyPrice, setMonthlyPrice] = useState<number | null>(null);
    const [compactUrl, setCompactUrl] = useState<string | null>(null);
    const [isBuyerProtectionOpen, setIsBuyerProtectionOpen] = useState(false);
    const partnershipParams = useSelector(selectPartnershipParams);
    const compact2IframeRef = useRef<HTMLIFrameElement>(null);

    const isMotors = adDetails?.l1Category === getL1CategoryIdBySeoName('cars-vans-motorbikes');
    const isForSale = adDetails?.l1Category === getL1CategoryIdBySeoName('for-sale');
    const isPets =
        adDetails?.leafCategoryId === L3CategoryIds.DOGS ||
        adDetails?.leafCategoryId === L3CategoryIds.CATS;
    const isPhonesForSale = Object.values(LeafCategoryIdsWithPhones).includes(
        adDetails?.leafCategoryId
    );
    const isDiyTools = L2CategoryIds.DIY_TOOLS_MATERIALS === adDetails?.l2CategoryId;
    const isHomeGarden = L2CategoryIds.HOME_GARDEN === adDetails?.l2CategoryId;
    const isJobs = adDetails?.l1Category === getL1CategoryIdBySeoName('jobs');
    const isUserLoggedIn = Boolean(userData?.userLoggedIn) || false;
    const { isPartnerAdCompact1Gam, isPartnerAdCompact2Gam, isPartnerAdDefault1Gam } =
        usePartnershipAdFlags();

    const isMobile = baseConfig.device?.type === 'mobile';
    const isMobileCardsLayout = isMobile && (isForSale || isMotors || isPets);
    const isServices = adDetails?.l1Category === L1CategoryIds.SERVICES;
    const isPurchaseAvailable =
        Array.isArray(adDetails?.attributes) &&
        adDetails.attributes.find((item) => item.key === 'support_shipping')?.value === 'True';

    const sellerType = adDetails?.sellerType;

    const hasImages = images?.length > 0;
    const hasVideo = video?.urls.length > 0;
    const showMapConditions = (!isSellerCinchCar && mapDetails?.visibleOnMap) || false;
    const mediaArray = hasVideo ? [...images, { link: video?.urls, type: 'video' }] : images;

    // emg cta optimization
    const isMotorsFeed = isMotors && adDetails.isFromMotorsFeed;
    const inExperimentMotorsCtaOpt = useFeatureValue(GrowthBookFeature.MOTORS_CTA_OPT, 'A') === 'B';
    const inExperimentMotorsPostCallEncourage =
        useFeatureValue(GrowthBookFeature.MOTORS_POST_CALL_ENCOURAGE, 'A') === 'B';
    const inExperimentMotorsOutOfCoreHour = useFeatureValue(
        GrowthBookFeature.MOTORS_OUT_OF_CORE_HOUR,
        'A'
    );
    const isMotorsCTATask1 = isMotorsFeed && inExperimentMotorsCtaOpt;
    const isMotorsCTATask2 = isMotorsFeed && inExperimentMotorsPostCallEncourage;
    const isMotorsCTATask3_B = isMotorsFeed && inExperimentMotorsOutOfCoreHour === 'B';
    const isMotorsCTATask3_C = isMotorsFeed && inExperimentMotorsOutOfCoreHour === 'C';
    const isMotorsCTATask3 = isMotorsCTATask3_B || isMotorsCTATask3_C;

    const showCarMoneyDefaultAboveMap =
        useFeatureValue(GrowthBookFeature.CARMONEY_ABOVE_MAP, 'A') === 'B' && isMotors && isMobile;
    /** Car finance calculator is too tall, therefore takes too much space */
    const isDefaultPartnershipBelowMap = isMotors && !showCarMoneyDefaultAboveMap;

    const ldJsonSchema = getPopulatedSchema(
        adDetails,
        images,
        baseConfig.currentPageUrl,
        baseConfig.domain
    );

    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            if (!validateOrigin(window, event.origin)) {
                return;
            }
            try {
                const data = JSON.parse(event.data);
                if (data.type === 'MONTHLY_PRICE_UPDATE') {
                    const { monthlyCost, compactUrl } = data.value;
                    setMonthlyPrice(monthlyCost);
                    setCompactUrl(compactUrl);

                    if (!window.clientData?.partnerships?.baseUrl) {
                        return;
                    }

                    const origin = window.clientData.partnerships.baseUrl;

                    compact2IframeRef.current?.contentWindow?.postMessage(
                        {
                            type: 'MONTHLY_PRICE_UPDATE',
                            value: { ...data.value, isQA: window.clientData?.baseConfig?.isQA },
                        },
                        origin
                    );
                }
            } catch {
                // ignore
            }
        };

        window.addEventListener('message', handleMessage);
        setCustomAttributeOnBraze(similarAds, adDetails.id);

        return () => window.removeEventListener('message', handleMessage);
    }, []);

    return (
        <QueryClientProvider client={queryClient}>
            <MotorsCTAContext.Provider
                value={{
                    isMotorsCTATask1,
                    isMotorsCTATask2,
                    isMotorsCTATask3,
                    isMotorsCTATask3_B,
                    isMotorsCTATask3_C,
                }}
            >
                <VipContainer isMobileCardsLayout={isMobileCardsLayout}>
                    <VipContent isMobileCardsLayout={isMobileCardsLayout}>
                        <StructuredData data={ldJsonSchema} />
                        <Grid item>
                            <AdBreadcrumbSlot>
                                <div>
                                    <AdSlot id={DisplayAdSlotDivNames.vipBanner} />
                                </div>
                                <Breadcrumbs links={breadcrumbs.links} />
                            </AdBreadcrumbSlot>
                            <AdSlot
                                id={DisplayAdSlotDivNames.pixel}
                                css={css`
                                    height: 0;
                                `}
                            />
                        </Grid>
                        {isPurchaseAvailable ? (
                            <BuyStickyHeader setIsBuyerProtectionOpen={setIsBuyerProtectionOpen} />
                        ) : (
                            <StickyHeader />
                        )}
                        {isMotorsCTATask1 && <AdSlot id={DisplayAdSlotDivNames.floatingTop} />}
                        {renderVipCarouselContainer({
                            isMobileCardsLayout,
                            isMobile,
                            adDetails,
                            mapDetails,
                            isPartnerAdCompact1Gam,
                            isPartnerAdCompact2Gam,
                            partnershipParams,
                            hasImages,
                            hasVideo,
                            isJobs,
                            replyEmail,
                            isMotors,
                            isSellerCinchCar,
                            isPurchaseAvailable,
                            monthlyPrice,
                            compactUrl,
                            compact2IframeRef,
                            sellerType,
                            setIsBuyerProtectionOpen,
                        })}
                        {RightPanelContainer({
                            isMobileCardsLayout,
                            isMobile,
                            isMotors,
                            isPets,
                            isPhonesForSale,
                            partnershipParams,
                            adDetails,
                            vhcData,
                            baseConfig,
                            isPartnerAdDefault1Gam,
                            mediaArray,
                            showMapConditions,
                            isDefaultPartnershipBelowMap,
                            isJobs,
                            isForSale,
                            mapDetails,
                            isPurchaseAvailable,
                            isDiyTools,
                            isHomeGarden,
                            setIsBuyerProtectionOpen,
                        })}
                        <Grid item colL={isMobile ? undefined : 6} colXl={isMobile ? undefined : 8}>
                            {renderVipAdInfo({
                                isServices,
                                isMobileCardsLayout,
                                isMotors,
                                isForSale,
                                isJobs,
                                isUserLoggedIn,
                                isPets,
                                isPhonesForSale,
                                isMobile,
                                partnershipParams,
                                adDetails,
                                baseConfig,
                                isPartnerAdDefault1Gam,
                                sellerSkills: sellerSkills || [],
                                isDiyTools,
                                isHomeGarden,
                            })}
                        </Grid>
                        <SocialPanelLeft /** Less than medium */ item>
                            {adDetails?.l1Category === 2526 && (
                                <PetsAdvicePanel isMobileCardsLayout={isMobileCardsLayout} />
                            )}

                            {!isMobileCardsLayout && (
                                <VipAdSocial>
                                    <SharePanel />
                                </VipAdSocial>
                            )}
                            <StaySafeContainer
                                isMobileCardsLayout={isMobileCardsLayout}
                                {...(baseConfig?.device?.type === 'desktop' && {
                                    'data-q': 'stay-safe-desktop',
                                })}
                            >
                                <StaySafe />
                            </StaySafeContainer>
                            {!isMobileCardsLayout && (
                                <AdSlot
                                    id={DisplayAdSlotDivNames.vipMiddleDesktop}
                                    css={css`
                                        margin: 18px 0;
                                        width: 100%;
                                    `}
                                />
                            )}
                        </SocialPanelLeft>
                        <div style={{ clear: 'both' }}>
                            <SimilarAdsCarouselContainer isMobileCardsLayout={isMobileCardsLayout}>
                                <SimilarAdsCarousel
                                    seeMoreLink={
                                        breadcrumbs &&
                                        breadcrumbs?.links[breadcrumbs.links.length - 1]?.url
                                    }
                                />
                            </SimilarAdsCarouselContainer>
                            {renderAdSlot(
                                DisplayAdSlotDivNames.fBanner,
                                adDetails.l1CategorySeoName,
                                baseConfig.device.type,
                                css`
                                    padding: 18px 0;
                                    border-top: 1px solid #d8d6d9;
                                    clear: both;
                                `
                            )}
                            <SeoL1Category />
                        </div>
                    </VipContent>
                    {!isMotorsCTATask1 && (
                        <StickyFooter>
                            <AdSlot id={DisplayAdSlotDivNames.floatingFooter} />
                        </StickyFooter>
                    )}
                    {isJobs && <MadgexConfirm advertId={adDetails.id} />}
                </VipContainer>
                {((isMobile && isMotorsCTATask2) || isMotorsCTATask3) && (
                    <MessageEncourage
                        isOpen={postCall.showMessageEncourage || postCall.showOutOfHoursMessage}
                    />
                )}
                <BuyerProtectionDialog
                    isOpen={isBuyerProtectionOpen}
                    onClose={() => setIsBuyerProtectionOpen(false)}
                />
            </MotorsCTAContext.Provider>
        </QueryClientProvider>
    );
};

App.defaultProps = {
    mapDetails: {},
};

export default App;
